import { type ReactNode } from "react";
import { useDroppable } from "@dnd-kit/core";
import { cn } from "@/lib/utils";

interface DroppableColumnProps {
  id: "left" | "right";
  /** Whether the column has any sections currently. Used to
   *  swap in the empty-state hint so the operator has somewhere
   *  to drop into when the column is empty. */
  isEmpty: boolean;
  children: ReactNode;
}

/**
 * Drop target wrapping a Company-detail layout column in edit
 * mode. Highlights when a section is dragged over it and renders
 * an empty-state hint ("Drop sections here") when the column is
 * otherwise empty. The wrapper itself is a `useDroppable` so the
 * dnd-kit collision detection can target the column as a whole
 * (drops past the last item / on an empty column).
 */
export function DroppableColumn({
  id,
  isEmpty,
  children,
}: DroppableColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const heading = id === "left" ? "Left column" : "Right column";

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "min-h-[200px] space-y-3 rounded-xl p-3 transition-colors duration-200",
        isOver
          ? "bg-indigo-50/70 ring-2 ring-dashed ring-indigo-200"
          : "bg-slate-50/40 ring-1 ring-dashed ring-slate-200",
      )}
    >
      <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
        {heading}
      </span>
      {isEmpty ? (
        <div className="rounded-lg border border-dashed border-slate-200 px-4 py-8 text-center text-xs text-slate-300">
          Drop sections here
        </div>
      ) : (
        children
      )}
    </div>
  );
}
